<script setup>
import { computed, ref } from 'vue'
import { copyToClipboard } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { chatRoleEnum, useChatStore } from '@/stores/chat.store'
import { useRelativeTime } from '@/composables/relative-time'

const COPIED_FEEDBACK_MS = 2000

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
})
const emit = defineEmits(['edit'])

const i18n = getI18n()
const chatStore = useChatStore()

const copied = ref(false)
const isSent = computed(() => props.message.role === chatRoleEnum.USER)
const { label: timeLabel, fullLabel: fullTimeLabel } = useRelativeTime(() => props.message.created_at)

const onCopy = async() => {
  await copyToClipboard(props.message.content)
  copied.value = true
  setTimeout(() => { copied.value = false }, COPIED_FEEDBACK_MS)
}
</script>

<template>
  <div class="chat-message-actions row items-center no-wrap" :class="{ 'justify-end chat-message-actions--sent': isSent }">
    <span class="chat-message-actions__time">
      {{ timeLabel }}
      <q-tooltip>{{ fullTimeLabel }}</q-tooltip>
    </span>
    <q-btn
      flat
      round
      dense
      size="sm"
      icon="mdi-refresh"
      :disable="!chatStore.canRewind(message)"
      @click="chatStore.regenerateAsync(message)"
    >
      <q-tooltip>{{ i18n.t('chat.regenerate') }}</q-tooltip>
    </q-btn>
    <q-btn
      v-if="isSent"
      flat
      round
      dense
      size="sm"
      icon="mdi-pencil-outline"
      :disable="!chatStore.canRewind(message)"
      @click="emit('edit', message)"
    >
      <q-tooltip>{{ i18n.t('chat.editMessage') }}</q-tooltip>
    </q-btn>
    <q-btn
      flat
      round
      dense
      size="sm"
      :icon="copied ? 'mdi-check' : 'mdi-content-copy'"
      :color="copied ? 'primary' : undefined"
      @click="onCopy"
    >
      <q-tooltip>{{ i18n.t('chat.copy') }}</q-tooltip>
    </q-btn>
  </div>
</template>

<style scoped lang="scss">
.chat-message-actions {
  gap: .125rem;
  margin-top: .25rem;
  color: $grey-6;
  opacity: 0.6;
  transition: opacity .2s ease;
  &:hover {
    opacity: 1;
  }
}

.chat-message-actions__time {
  font-size: .75rem;
  margin: 0 .25rem;
  font-variant-numeric: tabular-nums;
}
</style>
